import en from './en';
import de from './de';
import fr from './fr';
import es from './es';
import cs from './cs';
import { FALLBACK_LANG, isLang } from './languages';
import { safe } from '../lib/safe';
import type { MessageKey } from './en';
import type { Lang } from './languages';

export type { MessageKey } from './en';
export { LANGUAGES, FALLBACK_LANG, isLang } from './languages';
export type { Lang } from './languages';

const DICTS: Readonly<Record<Lang, Record<MessageKey, string>>> = { en, de, fr, es, cs };

const STORAGE_KEY = 'kuer.lang.v1';

function detectLang(): Lang {
  const stored = safe(() => localStorage.getItem(STORAGE_KEY), null);
  if (stored !== null && isLang(stored)) return stored;
  const nav = safe(() => navigator.language.slice(0, 2), '');
  if (isLang(nav)) return nav;
  return FALLBACK_LANG;
}

let lang: Lang = detectLang();
type Listener = () => void;
const listeners = new Set<Listener>();

export function currentLang(): Lang {
  return lang;
}

/** Change the UI language and notify subscribers. Persisted per browser —
 *  it's a device preference, not workspace data, so it does not go in the
 *  `.kuer.json` file. */
export function setLang(next: Lang): void {
  if (next === lang) return;
  lang = next;
  safe(() => localStorage.setItem(STORAGE_KEY, next), undefined);
  for (const fn of listeners) fn();
}

export function onLangChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Translate `key` for the current language, falling back to English for a
 * key a language file has not filled in yet at runtime (TypeScript already
 * prevents that at compile time for the five shipped languages, but a
 * dictionary loaded some other way could still be incomplete).
 * `{name}`-style placeholders in the string are replaced from `params`.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const dict = DICTS[lang] ?? DICTS[FALLBACK_LANG];
  let str = dict[key] ?? DICTS[FALLBACK_LANG][key] ?? key;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      str = str.split(`{${name}}`).join(String(value));
    }
  }
  return str;
}
